import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from '@/lib/axios';
import { debounce } from 'lodash';
import { Badge } from "@/components/ui/badge";
import { X, Loader2, ChevronsUpDown } from "lucide-react";

const SearchSelectCompanies = ({ onChange = () => { }, values = [], isSingle }) => {
    const [companies, setCompanies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCompanies, setSelectedCompanies] = useState(values);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchCompanies = async (query = '') => {
        try {
            setIsLoading(true);
            const response = await axios.get('/companies', {
                params: { search: query }
            });
            const companiesData = response.data?.data || [];
            setCompanies(companiesData);
            setError(null);
        } catch (err) {
            console.error('Error fetching companies:', err);
            setError(err.response?.data?.message || 'Failed to fetch companies');
            setCompanies([]);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedFetch = useCallback(
        debounce((query) => fetchCompanies(query), 300),
        []
    );

    useEffect(() => {
        if (isOpen) {
            debouncedFetch(searchQuery);
        }
    }, [searchQuery, isOpen]);

    const availableCompanies = companies.filter(company =>
        !selectedCompanies.some(selected => selected.id === company.id)
    );

    const handleSelect = (company) => {
        const previousComapanies = isSingle ? [] : [...selectedCompanies];
        const newSelection = [...previousComapanies, company];
        setSelectedCompanies(newSelection);
        setSearchQuery('');
        onChange?.(newSelection);
        inputRef.current?.focus();
    };

    const handleRemove = (companyId) => {
        const newSelection = selectedCompanies.filter(company => company.id !== companyId);
        setSelectedCompanies(newSelection);
        onChange?.(newSelection);
        inputRef.current?.focus();
    };

    return (
        <div ref={wrapperRef} className="relative w-full">
            <div
                onClick={() => {
                    setIsOpen(true);
                    inputRef.current?.focus();
                }}
                className="flex flex-wrap items-center gap-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
            >
                {selectedCompanies.map(company => (
                    <Badge
                        key={company.id}
                        variant="secondary"
                        className="flex items-center gap-1 text-xs"
                    >
                        {company.name}
                        <Badge className="bg-blue-500 ml-2" >
                            <span className='font-semibold'>ID: </span>
                            {company?.id}
                        </Badge>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemove(company.id);
                            }}
                            className="rounded-full hover:bg-secondary/80"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </Badge>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground min-w-[120px] text-sm"
                    placeholder={selectedCompanies.length === 0 ? "Search companies..." : ""}
                />
                <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0" />
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute w-full z-50 mt-1 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in">
                    <div className="max-h-[300px] overflow-auto p-1">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-6">
                                <Loader2 className="h-4 w-4 animate-spin" />
                            </div>
                        ) : availableCompanies.length === 0 ? (
                            <div className="text-sm text-muted-foreground text-center py-6">
                                {searchQuery ? "No companies found" : "Type to search companies"}
                            </div>
                        ) : (
                            availableCompanies.map((company) => (
                                <div
                                    key={company.id}
                                    onClick={() => handleSelect(company)}
                                    className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                                >
                                    {company.name}
                                    <Badge className="bg-blue-500 ml-2" >
                                        <span className='font-semibold'>ID: </span>
                                        {company?.id}
                                    </Badge>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchSelectCompanies;